import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';
import type { TimezoneDto } from '@/modules/counterparty/dto/timezone.dto.ts';
import type { Timezone } from '@/modules/counterparty/types/timezone.type.ts';

function asText(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function isPlusFromLegacyNegativeFlag(isNegative: boolean) {
  return !isNegative;
}

function isPlusFromApiSign(value: unknown) {
  if (value === 1 || value === '1') {
    return true;
  }

  if (value === 0 || value === '0' || value == null) {
    return false;
  }

  if (typeof value !== 'string') {
    return false;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === '+') {
    return true;
  }

  if (normalized === '-' || normalized === '') {
    return false;
  }

  if (normalized === 'true') {
    return isPlusFromLegacyNegativeFlag(true);
  }

  if (normalized === 'false') {
    return isPlusFromLegacyNegativeFlag(false);
  }

  return false;
}

export function isPlusSign(value: unknown) {
  if (typeof value === 'boolean') {
    return isPlusFromLegacyNegativeFlag(value);
  }

  return isPlusFromApiSign(value);
}

export function parseTimezoneOffsetMinutes(item: TimezoneDto) {
  const time = asText(item.Разница).trim().split(/\s+/u).at(-1) ?? '00:00:00';
  const [hoursPart = '0', minutesPart = '00'] = time.split(':');
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return 0;
  }

  const total = hours * 60 + minutes;
  return isPlusSign(item.Знак) ? total : -total;
}

function formatSignedOffset(offsetMinutes: number) {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absolute = Math.abs(offsetMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;

  if (minutes === 0) {
    return `${sign}${hours}`;
  }

  return `${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

export function formatTimezoneLabel(
  offsetMinutes: number,
  serverOffsetMinutes: number,
  description: string,
  serverRegion: string
) {
  const utc = `UTC${formatSignedOffset(offsetMinutes)}`;
  const relative = formatSignedOffset(offsetMinutes - serverOffsetMinutes);
  const cities = asText(description).trim();
  const region = asText(serverRegion).trim();
  const serverPart = region ? `(${region} ${relative})` : `(${relative})`;

  return cities ? `${utc} ${serverPart} ${cities}` : `${utc} ${serverPart}`;
}

export class TimezoneAdapter {
  private static map(response: TimezoneDto, serverOffsetMinutes: number, serverRegion: string): Timezone {
    const description = asText(response.Описание).trim();
    const offsetMinutes = parseTimezoneOffsetMinutes(response);

    return {
      id: Number(response.TZ_ID),
      description,
      offsetMinutes,
      name: formatTimezoneLabel(offsetMinutes, serverOffsetMinutes, description, serverRegion),
    };
  }

  static mapList(response: ItemsDto<TimezoneDto>, serverOffsetMinutes: number, serverRegion: string) {
    const items = Array.isArray(response.items) ? response.items : [];

    return items
      .map((item) => TimezoneAdapter.map(item, serverOffsetMinutes, serverRegion))
      .filter((zone) => Number.isFinite(zone.id))
      .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.id - b.id);
  }
}
